import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Get_All_Sales_Action } from '../Redux/Actions/SalesAction';
import { GetAllCampainData } from '../Redux/Actions/Campaign_Actions';
import { useSidebarWidth } from '../Utility/useSidebarWidth';
import { db_url } from '../Api/BaseUrl';

const REPORT_ENDPOINT   = `${db_url}api/v1/users/leads-with-sales-in-campaign-report`;
const GRAPH_ENDPOINT    = (id) => `${db_url}api/v1/users/campaign-daily-graph/${id}`;

/* ─────────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────────── */
const T = {
  navy:        '#0B1437',
  navyLight:   '#0F1D4A',
  accent:      '#0D47A1',
  accentBg:    'rgba(26,60,219,0.08)',
  white:       '#FFFFFF',
  offWhite:    '#f0f2f5',
  surface:     '#FFFFFF',
  border:      '#DDE3F0',
  borderLight: '#EEF1FA',
  text:        '#0B1437',
  textMuted:   '#6B7694',
  textLight:   '#9BA5C9',
  gold:        '#C8A96E',
  danger:      '#E24B4A',
  success:     '#1D9E75',
  warning:     '#BA7517',
  labelSize:   '11px',
  sectionFont: "'DM Sans', 'Georgia', sans-serif",
  bodyFont:    "'DM Sans', sans-serif",
  darkBg:      '#0f1117',
  darkSurface: '#1a1d2e',
  darkSurface2:'#161929',
  darkBorder:  '#2a2f45',
  darkText:    '#e5e7eb',
  darkMuted:   '#6b7280',
};

if (typeof document !== 'undefined' && !document.getElementById('azure-fonts')) {
  const link = document.createElement('link');
  link.id   = 'azure-fonts';
  link.rel  = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap';
  document.head.appendChild(link);
}

/* ─────────────────────────────────────────────
   TOOLTIP STYLE INJECTION (runs once)
───────────────────────────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('ms-tooltip-style')) {
  const style = document.createElement('style');
  style.id = 'ms-tooltip-style';
  style.textContent = `
    .ms-tooltip-box { display: none !important; }
    .ms-tooltip-trigger:hover .ms-tooltip-box { display: block !important; }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────
   DAILY LEADS CHART TOOLTIP
───────────────────────────────────────────── */
const DailyChartTooltip = ({ active, payload, isDark }) => {
  if (!active || !payload?.length) return null;
  const surface = isDark ? T.darkSurface2 : '#fff';
  const border  = isDark ? T.darkBorder : T.border;
  const textPri = isDark ? T.darkText : T.text;
  const textSec = isDark ? T.darkMuted : T.textMuted;
  const p = payload[0];

  return (
    <div style={{
      background: surface, border: `1px solid ${border}`, borderRadius: 12,
      padding: '10px 14px', boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
      fontFamily: T.bodyFont,
    }}>
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: textSec, textTransform: 'uppercase', letterSpacing: '1px' }}>
        {p.payload.date}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 800, color: T.accent }}>
        {p.value} <span style={{ fontSize: 11, fontWeight: 600, color: textSec }}>leads</span>
      </p>
    </div>
  );
};

/* ─────────────────────────────────────────────
   DAILY LEADS MINI CHART
───────────────────────────────────────────── */
const DailyLeadsChart = ({ campaignId, campaignName, isDark }) => {
  const [graphData, setGraphData] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [expanded,  setExpanded]  = useState(false);

  const fetchGraph = useCallback(async () => {
    if (graphData || loading) return;
    setLoading(true);
    setError('');
    try {
      const userRaw = localStorage.getItem('user');
      const token   = userRaw ? JSON.parse(userRaw)?.token : null;
      const res = await fetch(GRAPH_ENDPOINT(campaignId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (json.status === 'success') setGraphData(json.graph || []);
      else setError(json.message || 'Failed to load graph');
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [campaignId, graphData, loading]);

  useEffect(() => {
    if (expanded) fetchGraph();
  }, [expanded]);

  const borderColor = isDark ? T.darkBorder : T.border;
  const bg          = isDark ? T.darkSurface : T.surface;
  const gridColor   = isDark ? T.darkBorder : T.borderLight;
  const axisColor   = isDark ? T.darkMuted : T.textLight;

  const maxVal  = graphData?.length ? Math.max(...graphData.map((d) => d.count)) : 0;
  const peakDay = graphData?.length ? graphData.find((d) => d.count === maxVal) : null;
  const avgPerDay = graphData?.length
    ? (graphData.reduce((s, d) => s + d.count, 0) / graphData.length).toFixed(1)
    : 0;

  return (
    <div style={{
      marginBottom: '16px',
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      background: bg,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', background: 'transparent', border: 'none', cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${borderColor}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>📈</span>
          <span style={{
            fontSize: T.labelSize, fontWeight: '700', letterSpacing: '1.5px',
            textTransform: 'uppercase', color: T.accent, fontFamily: T.bodyFont,
          }}>
            Daily Leads — {campaignName}
          </span>
        </div>
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          style={{ transition: 'transform .2s', transform: expanded ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
        >
          <path d="M2 5l5 5 5-5" stroke={isDark ? T.darkMuted : T.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {expanded && (
        <div style={{ padding: '20px 20px 8px' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '32px 0' }}>
              <CircularProgress size={20} style={{ color: T.accent }} />
              <span style={{ fontSize: '13px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont }}>
                Loading chart data...
              </span>
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: '18px', textAlign: 'center', fontSize: '13px', color: T.danger, fontFamily: T.bodyFont }}>
              ⚠️ {error}
            </div>
          )}

          {!loading && !error && graphData && graphData.length === 0 && (
            <div style={{ padding: '28px', textAlign: 'center', fontSize: '13px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont }}>
              No lead data available since campaign start.
            </div>
          )}

          {!loading && !error && graphData && graphData.length >= 2 && (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={graphData} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`area-grad-${campaignId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={T.accent} stopOpacity="0.22" />
                      <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 3" stroke={gridColor} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d?.slice(5)}
                    tick={{ fontSize: 11, fill: axisColor, fontFamily: T.bodyFont }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: axisColor, fontFamily: T.bodyFont }}
                    axisLine={false} tickLine={false} allowDecimals={false}
                  />
                  <Tooltip
                    content={<DailyChartTooltip isDark={isDark} />}
                    cursor={{ stroke: T.accent, strokeWidth: 1, strokeDasharray: '4 3' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={T.accent}
                    strokeWidth={2}
                    fill={`url(#area-grad-${campaignId})`}
                    dot={{ r: 3, fill: T.accent, strokeWidth: 2, stroke: isDark ? T.darkSurface : T.white }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              <div style={{ display: 'flex', gap: '16px', marginTop: '6px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Days tracked', val: graphData.length },
                //   { label: 'Peak day', val: `${peakDay?.date?.slice(5) || '-'} (${maxVal})` },
                //   { label: 'Avg / day', val: avgPerDay },
                ].map(({ label, val }) => (
                  <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', color: isDark ? T.darkMuted : T.textLight, fontFamily: T.bodyFont }}>
                      {label}
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: isDark ? T.darkText : T.text, fontFamily: T.sectionFont }}>
                      {val}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   PAGINATION CONTROLS
───────────────────────────────────────────── */
const PaginationBar = ({ page, totalPages, totalCampaigns, limit, onPageChange, onLimitChange, isDark }) => {
  const borderColor = isDark ? T.darkBorder : T.border;
  const bg          = isDark ? T.darkSurface : T.surface;
  const mutedColor  = isDark ? T.darkMuted : T.textMuted;
  const textColor   = isDark ? T.darkText : T.text;

  const btnStyle = (active, disabled) => ({
    minWidth: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '8px', border: `1.5px solid ${active ? T.accent : borderColor}`,
    background: active ? T.accent : 'transparent',
    color: active ? '#fff' : disabled ? (isDark ? '#374151' : '#d1d5db') : textColor,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px', fontWeight: '600', fontFamily: T.bodyFont,
    transition: 'all .15s', opacity: disabled ? 0.5 : 1, padding: '0 8px',
  });

  const buildPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
      padding: '16px 20px', background: bg, border: `1px solid ${borderColor}`, borderRadius: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <span style={{ fontSize: '12px', color: mutedColor, fontFamily: T.bodyFont }}>
          {totalCampaigns} campaign{totalCampaigns !== 1 ? 's' : ''} found
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: mutedColor, fontFamily: T.bodyFont }}>
            Show
          </span>
          {[5, 10, 25, 50].map((n) => (
            <button
              key={n}
              onClick={() => { onLimitChange(n); onPageChange(1); }}
              style={{
                padding: '4px 10px', borderRadius: '6px', border: `1.5px solid ${limit === n ? T.accent : borderColor}`,
                background: limit === n ? T.accentBg : 'transparent',
                color: limit === n ? T.accent : mutedColor,
                fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: T.bodyFont,
                transition: 'all .15s',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => onPageChange(page - 1)} disabled={page === 1} style={btnStyle(false, page === 1)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {buildPages().map((p, i) =>
            p === '...'
              ? <span key={`dots-${i}`} style={{ padding: '0 4px', color: mutedColor, fontSize: '13px' }}>…</span>
              : (
                <button key={p} onClick={() => onPageChange(p)} style={btnStyle(p === page, false)}>
                  {p}
                </button>
              )
          )}

          <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages} style={btnStyle(false, page === totalPages)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   MINI COMPONENTS
───────────────────────────────────────────── */
const SectionTag = ({ number, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
    <div style={{ width: '28px', height: '2px', background: T.accent }} />
    <span style={{
      fontFamily: T.bodyFont, fontSize: T.labelSize, fontWeight: '700',
      letterSpacing: '2px', color: T.accent, textTransform: 'uppercase',
    }}>
      Section {String(number).padStart(2, '0')} / {label}
    </span>
  </div>
);

const FieldLabel = ({ children, isDark }) => (
  <label style={{
    display: 'block', marginBottom: '6px', fontFamily: T.bodyFont,
    fontSize: T.labelSize, fontWeight: '700', letterSpacing: '1.5px',
    textTransform: 'uppercase', color: isDark ? T.darkMuted : T.textMuted,
  }}>
    {children}
  </label>
);

const SectionCard = ({ children, isDark, style }) => (
  <div style={{
    background: isDark ? T.darkSurface : T.surface,
    border: `1px solid ${isDark ? T.darkBorder : T.border}`,
    borderRadius: '12px', padding: '28px', marginBottom: '20px', ...style,
  }}>
    {children}
  </div>
);

const LuxuryCheckbox = ({ checked, isDark }) => (
  <div style={{
    width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
    border: `1.5px solid ${checked ? T.accent : (isDark ? T.darkBorder : T.border)}`,
    background: checked ? T.accent : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all .15s',
  }}>
    {checked && (
      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5l2.5 2.5L8.5 2" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   LUXURY MULTI SELECT — tooltip version
   (الـ selected items تظهر في tooltip فوق الـ select
    ومش بتتراص تحته)
───────────────────────────────────────────── */
const LuxuryMultiSelect = ({
  options = [], selectedIds = [], onChange, placeholder, isDark,
  getLabel = (o) => o?.name || '',
  getIcon = () => '🏷',
  getSubtitle,
  idKey = '_id',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter((o) => getLabel(o).toLowerCase().includes(search.toLowerCase()));
  const selectedSet = new Set(selectedIds);
  const selectedOptions = options.filter((o) => selectedSet.has(o[idKey]));
  const toggle = (id) => selectedSet.has(id) ? onChange(selectedIds.filter((i) => i !== id)) : onChange([...selectedIds, id]);
  const selectAllFiltered = () => { const ids = new Set(selectedIds); filtered.forEach((o) => ids.add(o[idKey])); onChange(Array.from(ids)); };
  const clearAll = () => onChange([]);
  const removeOne = (id, e) => { e.stopPropagation(); onChange(selectedIds.filter((i) => i !== id)); };

  const tooltipBg     = isDark ? T.darkSurface : '#FFFFFF';
  const tooltipBorder = isDark ? T.darkBorder : T.border;
  const tooltipMuted  = isDark ? T.darkMuted : T.textMuted;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>

      {/* ── Tooltip (hover فوق الـ trigger يظهره) ── */}
      {selectedOptions.length > 0 && (
        <div
          className="ms-tooltip-trigger"
          style={{ position: 'relative' }}
        >
          {/* Tooltip box */}
          <div
            className="ms-tooltip-box"
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              background: tooltipBg,
              border: `1.5px solid ${tooltipBorder}`,
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              padding: '12px 14px',
              zIndex: 10000,
              pointerEvents: 'none',
            }}
          >
            <div style={{
              fontSize: '10px', fontWeight: '700', letterSpacing: '1.3px',
              textTransform: 'uppercase', color: tooltipMuted,
              marginBottom: '8px', fontFamily: T.bodyFont,
            }}>
              {selectedOptions.length} selected
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {selectedOptions.map((o) => (
                <span key={o[idKey]} style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '4px 10px', borderRadius: '20px',
                  background: isDark ? 'rgba(13,71,161,0.18)' : T.accentBg,
                  fontSize: '11px', fontWeight: '600', color: T.accent,
                  fontFamily: T.bodyFont,
                }}>
                  {getLabel(o)}
                </span>
              ))}
            </div>
          </div>

          {/* Trigger box (نفس شكل الـ select العادي) */}
          <div
            onClick={() => setOpen((o) => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px', minHeight: '42px',
              borderRadius: '10px',
              border: `1.5px solid ${open ? T.accent : isDark ? T.darkBorder : T.border}`,
              background: isDark ? T.darkSurface2 : '#FAFBFF',
              cursor: 'pointer', userSelect: 'none',
              boxShadow: open ? `0 0 0 3px rgba(13,71,161,0.08)` : 'none',
              transition: 'border-color .2s, box-shadow .2s',
            }}
          >
            <span style={{
              fontSize: '10px', fontWeight: '700', letterSpacing: '1px',
              color: T.accent, background: T.accentBg,
              padding: '4px 10px', borderRadius: '20px',
              fontFamily: T.bodyFont, flexShrink: 0,
            }}>
              {selectedOptions.length} SELECTED
            </span>
            <span style={{
              fontSize: '12px', color: isDark ? T.darkMuted : T.textMuted,
              fontFamily: T.bodyFont, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
            }}>
              {selectedOptions.slice(0, 2).map(getLabel).join(', ')}
              {selectedOptions.length > 2 ? ` +${selectedOptions.length - 2} more` : ''}
            </span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
              style={{ flexShrink: 0, marginLeft: 'auto', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>
              <path d="M2 4l4 4 4-4" stroke={isDark ? T.darkMuted : T.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}

      {/* ── Placeholder trigger (لما مفيش selection) ── */}
      {selectedOptions.length === 0 && (
        <div
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px', minHeight: '42px',
            borderRadius: '10px',
            border: `1.5px solid ${open ? T.accent : isDark ? T.darkBorder : T.border}`,
            background: isDark ? T.darkSurface2 : '#FAFBFF',
            cursor: 'pointer', userSelect: 'none',
            boxShadow: open ? `0 0 0 3px rgba(13,71,161,0.08)` : 'none',
            transition: 'border-color .2s, box-shadow .2s',
          }}
        >
          <span style={{ fontSize: '13px', color: isDark ? T.darkMuted : T.textLight, fontFamily: T.bodyFont }}>
            {placeholder || 'Select...'}
          </span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
            style={{ flexShrink: 0, marginLeft: 'auto', transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>
            <path d="M2 4l4 4 4-4" stroke={isDark ? T.darkMuted : T.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}

      {/* ── Dropdown list ── */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 9999,
          background: isDark ? T.darkSurface : '#FFFFFF',
          border: `1.5px solid ${isDark ? T.darkBorder : '#DDE3F0'}`,
          borderRadius: '12px', boxShadow: '0 12px 40px rgba(11,20,55,0.15)',
          overflow: 'hidden', maxHeight: '340px', display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${isDark ? T.darkBorder : '#EEF1FA'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', background: isDark ? T.darkSurface2 : '#F4F6FB' }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="6" cy="6" r="4.5" stroke={isDark ? T.darkMuted : T.textMuted} strokeWidth="1.3"/>
                <path d="M9.5 9.5l2 2" stroke={isDark ? T.darkMuted : T.textMuted} strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${placeholder || 'options'}...`}
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', fontFamily: T.bodyFont, flex: 1, color: isDark ? T.darkText : T.text }}
              />
              {search && (
                <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: isDark ? T.darkMuted : T.textLight, fontSize: '16px', lineHeight: 1 }}>×</span>
              )}
            </div>
          </div>

          {/* Select all / Clear */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 14px', borderBottom: `1px solid ${isDark ? T.darkBorder : T.borderLight}` }}>
            <span onClick={selectAllFiltered} style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', color: T.accent, cursor: 'pointer', textTransform: 'uppercase', fontFamily: T.bodyFont }}>
              Select All
            </span>
            <span onClick={clearAll} style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1px', color: isDark ? T.darkMuted : T.textMuted, cursor: 'pointer', textTransform: 'uppercase', fontFamily: T.bodyFont }}>
              Clear
            </span>
          </div>

          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont }}>
                No results found
              </div>
            ) : filtered.map((opt) => {
              const id = opt[idKey];
              const isSelected = selectedSet.has(id);
              return (
                <div
                  key={id}
                  onClick={() => toggle(id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                    cursor: 'pointer',
                    background: isSelected ? (isDark ? 'rgba(13,71,161,0.15)' : '#F0F4FF') : 'transparent',
                    borderLeft: isSelected ? `3px solid ${T.accent}` : '3px solid transparent',
                    transition: 'background .12s',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#F8F9FD'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? (isDark ? 'rgba(13,71,161,0.15)' : '#F0F4FF') : 'transparent'; }}
                >
                  <LuxuryCheckbox checked={isSelected} isDark={isDark} />
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '7px', flexShrink: 0,
                    background: isSelected ? (isDark ? 'rgba(13,71,161,0.25)' : '#DDEAFF') : (isDark ? T.darkBorder : '#EEF1FA'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px',
                  }}>
                    {getIcon(opt)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px', fontWeight: isSelected ? '600' : '500',
                      color: isSelected ? T.accent : (isDark ? T.darkText : T.text),
                      fontFamily: T.bodyFont, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {getLabel(opt)}
                    </div>
                    {getSubtitle && (
                      <div style={{
                        fontSize: '10px', letterSpacing: '1.2px', fontWeight: '700',
                        color: isSelected ? T.accent : (isDark ? T.darkMuted : T.textLight),
                        fontFamily: T.bodyFont, textTransform: 'uppercase', marginTop: '1px',
                      }}>
                        {getSubtitle(opt)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   STAT & COUNT COMPONENTS
───────────────────────────────────────────── */
const StatCard = ({ label, value, color, icon, isDark }) => (
  <div style={{
    flex: '1 1 150px', background: isDark ? T.darkSurface : T.surface,
    border: `1px solid ${isDark ? T.darkBorder : T.border}`, borderRadius: '12px',
    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px',
    borderTop: `3px solid ${color}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: T.labelSize, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont }}>
        {label}
      </span>
      <span style={{ fontSize: '15px' }}>{icon}</span>
    </div>
    <span style={{ fontFamily: T.sectionFont, fontSize: '30px', fontWeight: '700', color: isDark ? T.darkText : T.text, lineHeight: 1 }}>
      {value}
    </span>
  </div>
);

const CountPill = ({ value, color, isDark }) => (
  <div style={{ display: 'flex', justifyContent: 'center' }}>
    <span style={{
      minWidth: '36px', textAlign: 'center', padding: '4px 10px', borderRadius: '20px',
      fontSize: '12px', fontWeight: '700', fontFamily: T.bodyFont,
      background: value > 0 ? `${color}1A` : (isDark ? T.darkSurface2 : '#F4F6FB'),
      color: value > 0 ? color : (isDark ? T.darkMuted : T.textLight),
    }}>
      {value}
    </span>
  </div>
);

/* ─────────────────────────────────────────────
   CAMPAIGN RESULT CARD
───────────────────────────────────────────── */
const CampaignResultCard = ({ campaign, isDark }) => (
  <div style={{ marginBottom: '20px' }}>
    <DailyLeadsChart
      campaignId={campaign.campaignId}
      campaignName={campaign.campaignName}
      isDark={isDark}
    />

    <SectionCard isDark={isDark} style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: isDark ? T.darkBorder : '#EEF1FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flexShrink: 0 }}>
            📣
          </div>
          <div>
            <div style={{ fontFamily: T.sectionFont, fontSize: '18px', fontWeight: '700', color: isDark ? T.darkText : T.text }}>
              {campaign.campaignName}
            </div>
            <div style={{ fontSize: '10px', letterSpacing: '1.5px', color: isDark ? T.darkMuted : T.textLight, fontWeight: '700', textTransform: 'uppercase', fontFamily: T.bodyFont }}>
              CAMPAIGN ID · {String(campaign.campaignId).slice(-6).toUpperCase()}
            </div>
          </div>
        </div>
        <div style={{ padding: '7px 16px', borderRadius: '20px', background: T.accentBg, border: `1.5px solid ${T.accent}` }}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: T.accent, fontFamily: T.bodyFont }}>
            {campaign.totalLeads} TOTAL LEADS
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '10px', padding: '0 4px 10px', borderBottom: `1.5px solid ${isDark ? T.darkBorder : T.borderLight}` }}>
        {['SALES AGENT', 'TRASH', 'DATA CENTER', 'ORIGINAL', 'TOTAL'].map((h, i) => (
          <span key={h} style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '1.3px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont, textAlign: i === 0 ? 'left' : 'center' }}>
            {h}
          </span>
        ))}
      </div>

      {campaign.sales.map((s, idx) => (
        <div key={s.salesId || idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '10px', alignItems: 'center', padding: '14px 4px', borderBottom: idx === campaign.sales.length - 1 ? 'none' : `1px solid ${isDark ? T.darkBorder : T.borderLight}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: isDark ? T.darkBorder : '#EEF1FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 }}>
              👤
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: isDark ? T.darkText : T.text, fontFamily: T.bodyFont, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.salesName}
            </span>
          </div>
          <CountPill value={s.trash}      color={T.danger}  isDark={isDark} />
          <CountPill value={s.dataCenter} color={T.warning} isDark={isDark} />
          <CountPill value={s.original}   color={T.success} isDark={isDark} />
          <span style={{ textAlign: 'center', fontSize: '14px', fontWeight: '700', color: isDark ? T.darkText : T.text, fontFamily: T.bodyFont }}>
            {s.total}
          </span>
        </div>
      ))}
    </SectionCard>
  </div>
);

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export const LeadsWithSalesinCampaignPage = () => {
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const dispatch     = useDispatch();
  const sidebarWidth = useSidebarWidth();

  const [selectedSalesIds,    setSelectedSalesIds]    = useState([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);

  const [reportData,    setReportData]    = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [hasRunOnce,    setHasRunOnce]    = useState(false);
  const [fetchError,    setFetchError]    = useState('');

  const [page,           setPage]           = useState(1);
  const [limit,          setLimit]          = useState(10);
  const [totalPages,     setTotalPages]     = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);

  const [sortBy,    setSortBy]    = useState('totalLeads');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    dispatch(Get_All_Sales_Action());
    dispatch(GetAllCampainData());
  }, []);

  const ressales          = useSelector((s) => s.AllSales.sales);
  const resgetcampaindata = useSelector((s) => s.AllCampaign.campaign);

  const salesOptions = ressales?.data
    ? ressales.data.filter((s) => !['Default', 'Default M', 'Default T', 'Default S', 'Developer', 'Deveolper', 'Dev'].includes(s.name))
    : [];
  const campaignOptions = Array.isArray(resgetcampaindata?.data) ? resgetcampaindata.data : [];

  const runReport = useCallback(async (overridePage) => {
    setLoadingReport(true);
    setFetchError('');
    const currentPage = overridePage ?? page;
    try {
      const userRaw = localStorage.getItem('user');
      const token   = userRaw ? JSON.parse(userRaw)?.token : null;
      const res = await fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          salesIds: selectedSalesIds,
          campaignIds: selectedCampaignIds,
          page: currentPage,
          limit,
          sortBy,
          sortOrder,
        }),
      });
      const json = await res.json();
      if (json.status === 'success') {
        setReportData(json.data || []);
        setTotalPages(json.totalPages || 1);
        setTotalCampaigns(json.totalCampaigns || 0);
      } else {
        setFetchError(json.message || 'Something went wrong.');
        setReportData([]);
      }
    } catch (err) {
      setFetchError(err.message || 'Network error.');
      setReportData([]);
    } finally {
      setLoadingReport(false);
      setHasRunOnce(true);
    }
  }, [selectedSalesIds, selectedCampaignIds, page, limit, sortBy, sortOrder]);

  useEffect(() => { runReport(); }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    runReport(newPage);
    document.getElementById('results-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleRunReport = () => {
    setPage(1);
    runReport(1);
  };

  const clearFilters = () => {
    setSelectedSalesIds([]);
    setSelectedCampaignIds([]);
  };

  const totals = useMemo(() => reportData.reduce((acc, camp) => {
    acc.leads += camp.totalLeads || 0;
    (camp.sales || []).forEach((s) => {
      acc.trash      += s.trash || 0;
      acc.dataCenter += s.dataCenter || 0;
      acc.original   += s.original || 0;
    });
    return acc;
  }, { leads: 0, trash: 0, dataCenter: 0, original: 0 }), [reportData]);

  const showPagination = !loadingReport && hasRunOnce && reportData.length > 0;

  return (
    <div style={{
      position: 'fixed', top: '50px', left: sidebarWidth, right: 0, bottom: 0,
      transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      backgroundColor: isDark ? T.darkBg : T.offWhite,
      fontFamily: T.bodyFont,
    }}>

      {/* ── Breadcrumb ── */}
      <div style={{
        padding: '14px 28px', flexShrink: 0,
        backgroundColor: isDark ? T.darkSurface : T.offWhite,
        display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        borderBottom: `1px solid ${isDark ? T.darkBorder : T.border}`,
      }}>
        <Link to="/" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: '500' }}>Home</Link>
        <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
        <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: '600' }}>Campaign Performance Report</span>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'row' }}>

        {/* ══ LEFT PANEL — Filters ══ */}
        <div style={{
          width: '25%', flexShrink: 0,
          overflowY: 'auto', overflowX: 'hidden',
          padding: '24px 20px 24px 28px',
          borderRight: `1px solid ${isDark ? T.darkBorder : T.border}`,
        }}>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: T.labelSize, fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: T.accent, marginBottom: '6px', fontFamily: T.bodyFont }}>
              REPORTS / CAMPAIGN × SALES
            </div>
            <h1 style={{ fontFamily: T.sectionFont, fontSize: '28px', fontWeight: '700', color: isDark ? T.darkText : T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
              Leads by Sales &amp; Campaign
            </h1>
            <p style={{ margin: 0, fontSize: '13px', color: isDark ? T.darkMuted : T.textMuted, fontWeight: '300' }}>
              See exactly where every lead landed — Trash, Data Center, or Original — broken down by sales agent, with daily trends per campaign.
            </p>
          </div>

          <SectionCard isDark={isDark}>
            <SectionTag number={1} label="Filters" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '18px' }}>
              <div>
                <FieldLabel isDark={isDark}>Campaigns</FieldLabel>
                <LuxuryMultiSelect
                  options={campaignOptions}
                  selectedIds={selectedCampaignIds}
                  onChange={setSelectedCampaignIds}
                  placeholder="All campaigns"
                  isDark={isDark}
                  getLabel={(o) => o?.CampainName || ''}
                  getIcon={() => '📣'}
                  getSubtitle={() => 'CAMPAIGN'}
                />
              </div>
              <div>
                <FieldLabel isDark={isDark}>Sales Agents</FieldLabel>
                <LuxuryMultiSelect
                  options={salesOptions}
                  selectedIds={selectedSalesIds}
                  onChange={setSelectedSalesIds}
                  placeholder="All sales agents"
                  isDark={isDark}
                  getIcon={() => '👤'}
                  getSubtitle={(o) => o?.teamleader?.name ? `TEAM: ${o.teamleader.name.toUpperCase()}` : 'SALES AGENT'}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={clearFilters}
                style={{
                  padding: '10px 20px', background: 'transparent',
                  border: `1.5px solid ${isDark ? T.darkBorder : T.border}`,
                  borderRadius: '10px', color: isDark ? T.darkMuted : T.textMuted,
                  fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                  fontFamily: T.bodyFont, transition: 'all .2s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = isDark ? T.darkSurface2 : T.offWhite; }}
                onMouseOut={(e)  => { e.currentTarget.style.background = 'transparent'; }}
              >
                Clear Filters
              </button>
              <button
                onClick={!loadingReport ? handleRunReport : undefined}
                disabled={loadingReport}
                style={{
                  padding: '10px 22px', background: T.accent, border: 'none',
                  borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '600',
                  cursor: loadingReport ? 'not-allowed' : 'pointer', fontFamily: T.bodyFont,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  opacity: loadingReport ? 0.75 : 1, transition: 'all .2s',
                }}
                onMouseOver={(e) => { if (!loadingReport) e.currentTarget.style.opacity = '0.88'; }}
                onMouseOut={(e)  => { if (!loadingReport) e.currentTarget.style.opacity = '1'; }}
              >
                {loadingReport
                  ? <><CircularProgress size={15} style={{ color: 'white' }} /> Generating...</>
                  : <>Generate Report</>}
              </button>
            </div>
          </SectionCard>
        </div>

        {/* ══ RIGHT PANEL — Results ══ */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '24px 28px 0 20px' }}>

          <div style={{ flexShrink: 0 }}>
            <SectionTag number={2} label="Results" />
          </div>

          <div id="results-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>

            {loadingReport && reportData.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', padding: '60px 0' }}>
                <CircularProgress size={28} style={{ color: T.accent }} />
                <span style={{ fontSize: '13px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont }}>
                  Building report...
                </span>
              </div>
            )}

            {!loadingReport && fetchError && (
              <SectionCard isDark={isDark} style={{ borderColor: T.danger }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>⚠️</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: T.danger, fontFamily: T.bodyFont }}>
                      Couldn't load the report
                    </div>
                    <div style={{ fontSize: '12px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont, marginTop: '2px' }}>
                      {fetchError}
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {!loadingReport && !fetchError && hasRunOnce && reportData.length === 0 && (
              <SectionCard isDark={isDark}>
                <div style={{ textAlign: 'center', padding: '30px 0' }}>
                  <div style={{ fontSize: '30px', marginBottom: '10px' }}>📭</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: isDark ? T.darkText : T.text, fontFamily: T.bodyFont }}>
                    No leads found for the selected filters
                  </div>
                  <div style={{ fontSize: '12px', color: isDark ? T.darkMuted : T.textMuted, fontFamily: T.bodyFont, marginTop: '4px' }}>
                    Try clearing some filters or choosing different campaigns / sales agents.
                  </div>
                </div>
              </SectionCard>
            )}

            {loadingReport && reportData.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', background: T.accentBg, border: `1px solid ${T.accent}30` }}>
                <CircularProgress size={16} style={{ color: T.accent }} />
                <span style={{ fontSize: '12px', color: T.accent, fontFamily: T.bodyFont, fontWeight: '600' }}>
                  Loading page {page}…
                </span>
              </div>
            )}

            {!loadingReport && reportData.map((camp) => (
              <CampaignResultCard key={camp.campaignId} campaign={camp} isDark={isDark} />
            ))}

            <div style={{ height: '8px' }} />
          </div>

          {showPagination && (
            <div style={{ flexShrink: 0, marginTop: '12px', marginBottom: '12px' }}>
              <PaginationBar
                page={page} totalPages={totalPages} totalCampaigns={totalCampaigns}
                limit={limit} onPageChange={handlePageChange} onLimitChange={handleLimitChange} isDark={isDark}
              />
            </div>
          )}
        </div>

      </div>
    </div>
  );
};